import { useState, type ReactNode } from "react";

import {
  Alert,
  Close,
  Draft,
  Info,
  Offline,
  Success,
  Warning,
  type IconProps,
} from "./icons";
import { cn } from "./cn";

/**
 * Reserves the size of the final control so navigation and controls do not
 * jump when real content arrives.
 */
export function Skeleton({
  className,
  width,
  height,
}: {
  className?: string;
  width?: number | string;
  height?: number | string;
}) {
  return (
    <div
      aria-hidden="true"
      style={{ width, height }}
      className={cn("animate-skeleton rounded-xl bg-card-alt", className)}
    />
  );
}

export type StateTone = "accent" | "warning" | "error";

const RING_TONE: Record<StateTone, string> = {
  accent: "bg-accent-subtle text-accent-fg",
  warning: "bg-warning-bg text-warning",
  error: "bg-error-bg text-error",
};

/** 72px tinted ring with a 32px glyph, shared by empty/error/recovery screens. */
export function IconRing({
  icon: Glyph,
  tone = "accent",
}: {
  icon: (props: IconProps) => ReactNode;
  tone?: StateTone;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "mb-2 flex size-18 shrink-0 items-center justify-center rounded-full",
        RING_TONE[tone],
      )}
    >
      <Glyph size={32} />
    </span>
  );
}

interface StateProps {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: (props: IconProps) => ReactNode;
  iconTone?: StateTone;
}

const STATE_LAYOUT =
  "flex flex-col items-center justify-center gap-2 px-5 py-8 text-center";

export function EmptyState({
  title,
  body,
  action,
  icon,
  iconTone,
}: StateProps) {
  return (
    <div className={STATE_LAYOUT}>
      {icon && <IconRing icon={icon} tone={iconTone} />}
      <p className="text-headline text-fg">{title}</p>
      {body && <p className="text-callout text-fg-muted">{body}</p>}
      {action && <div className="mt-2 w-full max-w-sm">{action}</div>}
    </div>
  );
}

const REFERENCE_LENGTH = 8;

/**
 * Short support reference for a failure. The shown code is the id prefix; the
 * button copies the full id so support can search the logs for it.
 */
export function CorrelationRef({
  correlationId,
  label = "ID",
  copyLabel = "Copy",
  copiedLabel = "Copied",
}: {
  correlationId: string;
  label?: string;
  copyLabel?: string;
  copiedLabel?: string;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard
      .writeText(correlationId)
      .then(() => {
        setCopied(true);
      })
      .catch(() => {
        setCopied(false);
      });
  };
  return (
    <p className="text-footnote text-fg-subtle">
      {label}:{" "}
      <button
        type="button"
        onClick={copy}
        aria-label={`${copyLabel}: ${correlationId}`}
        title={correlationId}
        className="font-mono underline decoration-dotted underline-offset-2"
      >
        {correlationId.slice(0, REFERENCE_LENGTH)}
      </button>
      {copied && <span role="status"> {copiedLabel}</span>}
    </p>
  );
}

export function ErrorState({
  title,
  body,
  /** Support handle for the failure. Shown verbatim, never parsed. */
  correlationId,
  correlationLabel = "ID",
  copyLabel,
  copiedLabel,
  action,
  icon,
  iconTone = "error",
}: StateProps & {
  correlationId?: string;
  correlationLabel?: string;
  copyLabel?: string;
  copiedLabel?: string;
}) {
  return (
    <div role="alert" className={STATE_LAYOUT}>
      {icon && <IconRing icon={icon} tone={iconTone} />}
      <p className="text-headline text-fg">{title}</p>
      {body && <p className="text-callout text-fg-muted">{body}</p>}
      {correlationId && (
        <CorrelationRef
          correlationId={correlationId}
          label={correlationLabel}
          copyLabel={copyLabel}
          copiedLabel={copiedLabel}
        />
      )}
      {action && <div className="mt-2 w-full max-w-sm">{action}</div>}
    </div>
  );
}

export type BannerTone =
  "draft" | "offline" | "warning" | "info" | "error" | "success";

const BANNER_TONE: Record<
  BannerTone,
  { className: string; icon: (props: IconProps) => ReactNode }
> = {
  // banner.draft: permanently visible, never offered as dismissible.
  draft: { className: "bg-system-subtle text-system-fg", icon: Draft },
  offline: { className: "bg-warning-bg text-warning", icon: Offline },
  warning: { className: "bg-warning-bg text-warning", icon: Warning },
  info: { className: "bg-info-bg text-info", icon: Info },
  error: { className: "bg-error-bg text-error", icon: Alert },
  success: { className: "bg-success-bg text-success", icon: Success },
};

export function Banner({
  tone = "info",
  title,
  children,
  action,
  onDismiss,
  dismissLabel = "Close",
}: {
  tone?: BannerTone;
  /** Bold first line; without it the body is the headline. */
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}) {
  const { className, icon: Glyph } = BANNER_TONE[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      data-print="hidden"
      className={cn("flex gap-3 rounded-[18px] px-3.5 py-3", className)}
    >
      <Glyph size={18} className="mt-px shrink-0" />
      <div className="min-w-0 flex-1">
        {title && <p className="text-callout font-bold">{title}</p>}
        {children && (
          <p className={cn("text-callout", title ? "mt-0.5" : "font-semibold")}>
            {children}
          </p>
        )}
        {action && (
          <div className="mt-3 flex flex-wrap gap-2 [&_button]:bg-raised! [&_button]:text-fg! [&_button]:shadow-[inset_0_0_0_1px_var(--color-line-subtle)]">
            {action}
          </div>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="-m-3 flex size-11 shrink-0 items-center justify-center text-fg-muted"
        >
          <Close size={16} />
        </button>
      )}
    </div>
  );
}

/**
 * A command that cannot run right now. The design system forbids a bare grey
 * button: the reason is always stated next to it.
 */
export function UnavailableCommand({
  control,
  reason,
  unavailableLabel = "Unavailable",
}: {
  control: ReactNode;
  reason: string;
  unavailableLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      {control}
      <p className="flex items-center gap-1 text-caption text-warning">
        <Alert size={13} />
        {unavailableLabel}: {reason}
      </p>
    </div>
  );
}
